/**
 * Connection Drop Menu Hook
 * Manages context menu when connection is dropped in empty space
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { Edge } from '@xyflow/react'
import type { GenieCanvasNode } from '@/lib/canvas/types'
import { createTextFileNode, createImageFileNode } from '@/lib/canvas'

export interface ConnectionDropMenuState {
  show: boolean
  position: { x: number; y: number }
  sourceNodeId: string
  sourceHandleId: string
  sourceHandleType: 'input' | 'output'
  sourceHandleDataType: string
  dropPosition: { x: number; y: number }
}

export interface UseConnectionDropMenuProps {
  viewport: { x: number; y: number; zoom: number }
  canvasSize: { width: number; height: number }
  edges: Edge[]
  nodes: GenieCanvasNode[]
  canvasId?: string // For backend file uploads
  onEdgesChange?: (edges: Edge[]) => void
  onNodesChange?: (nodes: GenieCanvasNode[]) => void
}

export function useConnectionDropMenu({
  viewport,
  canvasSize,
  edges,
  nodes,
  canvasId,
  onEdgesChange,
  onNodesChange,
}: UseConnectionDropMenuProps) {
  const [menuState, setMenuState] = useState<ConnectionDropMenuState | null>(null)

  // Use refs to avoid re-creating callbacks on every render
  const viewportRef = useRef(viewport)
  const canvasSizeRef = useRef(canvasSize)
  const edgesRef = useRef(edges)
  const nodesRef = useRef(nodes)
  const canvasIdRef = useRef(canvasId)
  const onEdgesChangeRef = useRef(onEdgesChange)
  const onNodesChangeRef = useRef(onNodesChange)

  // Keep refs up to date
  useEffect(() => {
    viewportRef.current = viewport
    canvasSizeRef.current = canvasSize
    edgesRef.current = edges
    nodesRef.current = nodes
    canvasIdRef.current = canvasId
    onEdgesChangeRef.current = onEdgesChange
    onNodesChangeRef.current = onNodesChange
  })

  // Handle connection dropped callback (stable - no deps)
  const handleConnectionDropped = useCallback((params: {
    sourceNodeId: string
    sourceHandleId: string
    sourceHandleType: 'input' | 'output'
    sourceHandleDataType: string
    position: { x: number; y: number }
  }) => {
    // Convert world position to screen position for context menu
    const vp = viewportRef.current
    const cs = canvasSizeRef.current
    const screenPosition = {
      x: params.position.x * vp.zoom + vp.x + cs.width / 2,
      y: params.position.y * vp.zoom + vp.y + cs.height / 2,
    }

    setMenuState({
      show: true,
      position: screenPosition,
      sourceNodeId: params.sourceNodeId,
      sourceHandleId: params.sourceHandleId,
      sourceHandleType: params.sourceHandleType,
      sourceHandleDataType: params.sourceHandleDataType,
      dropPosition: params.position, // World coordinates for node creation
    })
  }, [])

  // Handle context menu selection - create node and complete connection (stable - uses menuState)
  const handleNodeTypeSelect = useCallback((nodeType: 'text-file' | 'image-file') => {
    if (!menuState) return

    // Create appropriate node at drop position
    const newNode = nodeType === 'text-file'
      ? createTextFileNode(menuState.dropPosition)
      : createImageFileNode(menuState.dropPosition)

    // Inject canvasId into node state for backend file uploads
    if (canvasIdRef.current && newNode.component?.state) {
      newNode.component.state.canvasId = canvasIdRef.current
    }

    // Add node directly via onNodesChange (bypass addNode API to preserve position)
    const updatedNodes = [...nodesRef.current, newNode]
    onNodesChangeRef.current?.(updatedNodes)

    // Find the appropriate target handle on the new node
    const targetHandle = newNode.handles?.find(h =>
      h.type !== menuState.sourceHandleType &&
      (h.dataType === menuState.sourceHandleDataType || h.dataType === 'any' || menuState.sourceHandleDataType === 'any')
    )

    if (targetHandle) {
      // Create edge between source and new node
      const newEdge: Edge = {
        id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'bezier' as const,
        source: menuState.sourceNodeId,
        target: newNode.id,
        sourceHandle: menuState.sourceHandleId,
        targetHandle: targetHandle.id,
        animated: false
      }

      // Add edge
      const updatedEdges = [...edgesRef.current, newEdge]
      onEdgesChangeRef.current?.(updatedEdges)
    }

    // Close context menu
    setMenuState(null)
  }, [menuState])

  // Close menu
  const closeMenu = useCallback(() => {
    setMenuState(null)
  }, [])

  // Get available node types based on dataType
  const getAvailableNodeTypes = useCallback(() => {
    if (!menuState) return []

    const dataType = menuState.sourceHandleDataType

    // Both text-file and image-file have output handles with 'any' dataType
    // So they can connect to any input handle
    const options: Array<{ type: 'text-file' | 'image-file'; label: string; icon: string }> = []

    // Text-file can provide text (dataType: 'any' but conceptually text)
    if (dataType === 'text' || dataType === 'any') {
      options.push({ type: 'text-file', label: 'Add Text File', icon: 'file-text' })
    }

    // Image-file can provide image (dataType: 'any' but conceptually image)
    if (dataType === 'image' || dataType === 'any') {
      options.push({ type: 'image-file', label: 'Add Image File', icon: 'image' })
    }

    // If both are compatible, show both
    if (options.length === 0) {
      // Fallback: show both if dataType is unknown
      options.push(
        { type: 'text-file', label: 'Add Text File', icon: 'file-text' },
        { type: 'image-file', label: 'Add Image File', icon: 'image' }
      )
    }

    return options
  }, [menuState])

  return {
    menuState,
    handleConnectionDropped,
    handleNodeTypeSelect,
    closeMenu,
    getAvailableNodeTypes,
  }
}
